import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import type { LocationCredentialResponse } from "../../../../utils/models";
import { facilityToString } from "../../../../utils/utilFunctions";

export type CredentialSortKey = keyof Pick<LocationCredentialResponse, "id" | "department_location_id" | "created_at" | "expires_at" | "status">;

export const useSynergyCredentialsState = () => {
	const {
		chosenDepartment: { locations },
	} = useChosenDepartment();
	const { t } = useTranslation();
	const [popoverOpen, setPopOverOpen] = useState(false);
	const [selectedCredentialForDelete, setSelectedCredentialForDelete] = useState<string | null>(null);
	const [sortKey, setSortKey] = useState<CredentialSortKey>("expires_at");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

	const handleTogglePopover = useCallback(() => {
		setPopOverOpen((prevState) => !prevState);
	}, []);

	const handleDeleteClick = useCallback((credentialId: string) => {
		setSelectedCredentialForDelete(credentialId);
	}, []);

	const handleDeleteCancel = useCallback(() => {
		setSelectedCredentialForDelete(null);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		setSelectedCredentialForDelete(null);
	}, []);

	const formattedLocationNameForRow = useCallback(
		(locationId: string) => {
			const found = locations.find((loc) => loc.id === locationId);
			return found ? facilityToString(found) : t("credential.unknown.location", { defaultValue: "Unknown Location" });
		},
		[locations, t],
	);

	const handleSort = useCallback((newSortKey: CredentialSortKey, direction: "asc" | "desc") => {
		setSortKey(newSortKey);
		setSortDirection(direction);
	}, []);

	return {
		isPopoverOpen: popoverOpen,
		handleTogglePopover,
		selectedCredentialForDelete,
		showDeleteConfirmation: !!selectedCredentialForDelete,
		handleDeleteClick,
		handleDeleteCancel,
		handleDeleteConfirm,
		formattedLocationNameForRow,
		sortKey,
		sortDirection,
		handleSort,
	};
};
